(() => {
  if (window.__gempagesCartRefreshBridge) {
    return;
  }

  window.__gempagesCartRefreshBridge = true;

  const CART_ADD_PATH = /\/cart\/add(?:\.js)?(?:[?#].*)?$/;
  const CART_DRAWER_OPEN_SELECTOR = 'modal-open[target="cart-drawer"], a[href$="/cart"], a[href*="/cart?"]';
  const CART_COUNT_SELECTORS = [
    '[id^="cartNavigationCounter-"]',
    '[id^="cartCounter-"]',
    '[id^="cartMobileCounter-"]'
  ].join(',');
  const GEM_PAGES_ADD_SELECTOR = '.gp-button-atc, button[name="add"]';
  const GEM_PAGES_PRODUCT_BUTTON_SELECTOR = 'gp-product-button, .gp-product-button';
  const MESSAGE_FIX_DELAYS = [80, 250, 600, 1200];
  const DEFAULT_SUCCESS_MESSAGE = 'Add product to cart successfully';
  const DEFAULT_ERROR_MESSAGE = 'Cannot add product to cart';

  let lastCartAddAt = 0;
  let lastGemPagesMessages = {
    success: DEFAULT_SUCCESS_MESSAGE,
    error: DEFAULT_ERROR_MESSAGE
  };
  let lastGemPagesMessage = DEFAULT_SUCCESS_MESSAGE;

  const getUrl = (resource) => {
    if (typeof resource === 'string') {
      return resource;
    }

    if (resource instanceof URL) {
      return resource.pathname + resource.search + resource.hash;
    }

    return resource?.url || '';
  };

  const isCartAddUrl = (resource) => {
    const url = getUrl(resource);

    try {
      return CART_ADD_PATH.test(new URL(url, window.location.origin).pathname);
    } catch (_error) {
      return CART_ADD_PATH.test(url);
    }
  };

  const hasRecentGemPagesAdd = () => Date.now() - lastCartAddAt <= 6000;

  const parseGemPagesButtonMessages = (productButton) => {
    if (!productButton) {
      return lastGemPagesMessages;
    }

    try {
      const data = JSON.parse(productButton.getAttribute('gp-data') || '{}');
      const setting = data.setting || {};

      return {
        success: setting.successMessage || DEFAULT_SUCCESS_MESSAGE,
        error: setting.errorMessage || DEFAULT_ERROR_MESSAGE
      };
    } catch (_error) {
      return lastGemPagesMessages;
    }
  };

  const isGemPagesAddToCartTarget = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    const addButton = target.closest(GEM_PAGES_ADD_SELECTOR);

    return Boolean(addButton?.closest(GEM_PAGES_PRODUCT_BUTTON_SELECTOR));
  };

  const trackGemPagesAddToCartTarget = (target) => {
    const productButton = target.closest(GEM_PAGES_PRODUCT_BUTTON_SELECTOR);

    lastCartAddAt = Date.now();
    lastGemPagesMessages = parseGemPagesButtonMessages(productButton);
    lastGemPagesMessage = lastGemPagesMessages.success;
  };

  const updateCartCountElements = (itemCount) => {
    document.querySelectorAll(CART_COUNT_SELECTORS).forEach((element) => {
      const currentBadge = element.querySelector('span');

      if (itemCount > 0) {
        const badge = currentBadge || document.createElement('span');

        badge.textContent = itemCount;

        if (!currentBadge) {
          const templateClass = element.id.includes('Navigation')
            ? '#navigation-bar-item-counter'
            : element.id.includes('Mobile')
              ? '#header-mobile-cart-counter'
              : '#header-toolbar-item-counter';

          badge.className = templateClass;
          element.replaceChildren(badge);
        }

        return;
      }

      element.replaceChildren();
    });
  };

  const refreshCartCount = async () => {
    try {
      const response = await fetch(`${window.dynamicURLs?.cart || '/cart'}.js`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const cart = await response.json();
      updateCartCountElements(cart.item_count || 0);
    } catch (_error) {
      // The add request already succeeded; failing to update the badge is non-blocking.
    }
  };

  const refreshCartDrawerOnOpen = () => {
    document.dispatchEvent(new Event('cart:refresh'));
  };

  const isIgnoredTextNode = (node) => {
    return Boolean(node.parentElement?.closest('script, style, template, svg'));
  };

  const updateMessageTextNode = (node) => {
    if (!node?.nodeValue || isIgnoredTextNode(node)) {
      return false;
    }

    if (node.nodeValue.trim() !== 'undefined') {
      return false;
    }

    node.nodeValue = node.nodeValue.replace('undefined', lastGemPagesMessage);
    node.parentElement?.setAttribute('data-gempages-cart-message-patched', 'true');

    return true;
  };

  const updatePatchedMessageElement = (element) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const node = walker.nextNode();

    if (node && !isIgnoredTextNode(node)) {
      node.nodeValue = lastGemPagesMessage;
    }
  };

  const fixUndefinedGemPagesMessages = () => {
    if (!hasRecentGemPagesAdd()) {
      return;
    }

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
      updateMessageTextNode(node);
      node = walker.nextNode();
    }

    document.querySelectorAll('[data-gempages-cart-message-patched="true"]').forEach(updatePatchedMessageElement);
  };

  const scheduleUndefinedMessageFix = () => {
    MESSAGE_FIX_DELAYS.forEach((delay) => {
      window.setTimeout(fixUndefinedGemPagesMessages, delay);
    });
  };

  document.addEventListener('click', (event) => {
    if (isGemPagesAddToCartTarget(event.target)) {
      trackGemPagesAddToCartTarget(event.target);
    }

    if (event.target instanceof Element && event.target.closest(CART_DRAWER_OPEN_SELECTOR)) {
      window.setTimeout(refreshCartDrawerOnOpen, 250);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (form instanceof HTMLFormElement && form.querySelector(GEM_PAGES_PRODUCT_BUTTON_SELECTOR)) {
      trackGemPagesAddToCartTarget(form.querySelector(GEM_PAGES_PRODUCT_BUTTON_SELECTOR));
    }
  }, true);

  if (window.fetch) {
    const originalFetch = window.fetch;

    window.fetch = async function cartRefreshFetchBridge(...args) {
      const response = await originalFetch.apply(this, args);

      if (isCartAddUrl(args[0])) {
        if (hasRecentGemPagesAdd()) {
          lastGemPagesMessage = response.ok ? lastGemPagesMessages.success : lastGemPagesMessages.error;
          scheduleUndefinedMessageFix();
        }

        if (response.ok) {
          await refreshCartCount();
        }
      }

      return response;
    };
  }

  if (window.XMLHttpRequest) {
    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function cartRefreshOpenBridge(method, url, ...args) {
      this.__gempagesCartRefreshUrl = url;
      return originalOpen.call(this, method, url, ...args);
    };

    window.XMLHttpRequest.prototype.send = function cartRefreshSendBridge(...args) {
      this.addEventListener('load', () => {
        if (isCartAddUrl(this.__gempagesCartRefreshUrl)) {
          if (hasRecentGemPagesAdd()) {
            lastGemPagesMessage = this.status >= 200 && this.status < 300
              ? lastGemPagesMessages.success
              : lastGemPagesMessages.error;
            scheduleUndefinedMessageFix();
          }

          if (this.status >= 200 && this.status < 300) {
            refreshCartCount();
          }
        }
      });

      return originalSend.apply(this, args);
    };
  }
})();
